'use client';

import React, { useId, useState } from 'react';
import { cn } from '@/lib/cn';
import { Skeleton } from '@/components/ui';

export interface ListingNotificationProps {
  className?: string;
  /** Uncontrolled: initial on/off */
  defaultOn?: boolean;
  /** Controlled: current value */
  on?: boolean;
  onChange?: (on: boolean) => void;
  /** Fetches preference from the API */
  isLoading?: boolean;
  isSaving?: boolean;
  /**
   * Override the row title text. Defaults to "New listing notifications"
   * for legacy call sites; the renter-profile hub passes
   * "Instant listing alerts" to match the Figma spec.
   */
  title?: string;
  /**
   * Override the row subtitle text. When provided it replaces BOTH
   * of the built-in on/off variants — pass a static line like
   * "Based on location, price, and bedrooms" that reads correctly
   * regardless of the toggle state.
   */
  subtitle?: string;
}

/**
 * “New listing notifications” row: copy + pill toggle
 * (off: navy track + white ring; on: accent fill, white thumb).
 * Wire `on` / `onChange` for persistence.
 */
export function ListingNotification({
  className,
  defaultOn = false,
  on: onProp,
  onChange,
  isLoading = false,
  isSaving = false,
  title,
  subtitle,
}: ListingNotificationProps) {
  const isControlled = onProp !== undefined;
  const [uncontrolledOn, setUncontrolledOn] = useState(defaultOn);
  const on = isControlled ? onProp : uncontrolledOn;

  const setOn = (next: boolean) => {
    if (isLoading || isSaving) return;
    onChange?.(next);
    if (!isControlled) {
      setUncontrolledOn(next);
    }
  };

  const textId = useId();
  const switchId = useId();

  return (
    <div
      className={cn('flex w-full min-w-0 items-center justify-between gap-4 py-3 px-2', className)}
    >
      <div className="min-w-0 flex-1" id={textId}>
        <p className="text-body-2 text-primary">{title ?? 'New listing notifications'}</p>
        <p className="text-caption text-secondary pt-1">
          {subtitle ??
            (on
              ? 'We’ll notify you when new matching apartments are posted.'
              : 'Turn on to get notified when new apartments match your search.')}
        </p>
      </div>
      {isLoading ? (
        <Skeleton className="h-7 w-12 shrink-0 rounded-full" aria-hidden />
      ) : (
        <button
          type="button"
          id={switchId}
          role="switch"
          aria-checked={on}
          aria-busy={isSaving}
          aria-disabled={isSaving}
          disabled={isSaving}
          aria-labelledby={textId}
          onClick={() => setOn(!on)}
          className={cn(
            'flex h-7 w-12 shrink-0 items-center rounded-full border p-0.5 transition-colors',
            on ? 'justify-end' : 'justify-start',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white',
            'disabled:opacity-60',
            on ? 'border-indigo-300/35 bg-accent' : 'border-white bg-[#1D2433]',
          )}
        >
          <span
            className={cn(
              'h-5 w-5 shrink-0 rounded-full bg-white shadow-sm transition-[transform,box-shadow] ring-1 ring-black/5',
            )}
          />
        </button>
      )}
    </div>
  );
}

export default ListingNotification;
